import clsx from 'clsx';
import { Button as PrimeButton } from 'primereact/button';
import { Toast } from 'primereact/toast';
import type { ToastMessage } from 'primereact/toast';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
} from 'react';
import { Button } from '../../Button/Button';
import { Icon } from '../../Icon/Icon';
import { PillStepper } from '../../Stepper/Stepper';
import { ToastContent } from '../../Toast/ToastContent';
import { DialogSize } from '../constraints';
import {
    StepIndexProvider,
    StepperDialogContext,
    StepperDialogStepsContext,
    type SetPrimaryDisabledOptions,
} from '../contexts/StepperDialogContext';
import { ConfirmCloseConfig, StepConfig, StepperDialogConfig } from '../types';
import { CloseConfirmationDialog } from './CloseConfirmationDialog';
import {
    DialogWithDoubleEscape,
    useDialogClose,
} from './DialogWithDoubleEscape';

interface StepperDialogProps extends Omit<StepperDialogConfig, 'type'> {
    onClose: () => void;
    loading: boolean;
    setLoading: (loading: boolean) => void;
}

interface StepHeaderState {
    reached: boolean;
    completed: boolean;
    nextAccessible: boolean;
    invalid: boolean;
    disabled: boolean;
    canInteract: boolean;
}

interface GoToStepOptions {
    keepToast?: boolean;
}

export function StepperDialog({
    steps,
    title,
    subtitle,
    allStepsReached,
    linear = true,
    onClose,
    size = DialogSize.md,
    loading,
    setLoading,
    pt,
    buttons,
    dialogClassName: propsDialogClassName,
    confirmClose,
    wrapper,
}: StepperDialogProps): ReactNode {
    const [dialogSteps, setDialogSteps] = useState(steps);
    const [activeStep, setActiveStep] = useState(0);
    const [maxReachedStep, setMaxReachedStep] = useState(
        linear
            ? allStepsReached
                ? dialogSteps.length - 1
                : 0
            : dialogSteps.length - 1,
    );
    const [completedMap, setCompletedMap] = useState<Record<number, boolean>>(
        () => {
            if (!linear || !allStepsReached) {
                return {};
            }

            return Array.from(
                { length: Math.max(dialogSteps.length - 1, 0) },
                (_, index) => index,
            ).reduce<Record<number, boolean>>((acc, index) => {
                acc[index] = true;
                return acc;
            }, {});
        },
    );
    const [disabledMap, setDisabledMap] = useState<Record<number, boolean>>({});
    const [invalidSteps, setInvalidSteps] = useState<number[]>([]);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const handlersRef = useRef<
        Record<number, () => boolean | Promise<boolean>>
    >({});
    const toastRef = useRef<Toast>(null);
    const skipNextToastClearRef = useRef(false);
    const stepEverValidRef = useRef<Set<number>>(new Set());
    const isSubmittingRef = useRef(false);
    const closeRef = useRef<(() => void) | undefined>(undefined);

    const forceClose = useCallback((): void => {
        (closeRef.current ?? onClose)();
    }, [onClose]);

    useEffect(() => {
        setDialogSteps(steps);
    }, [steps]);

    useEffect(() => {
        const lastStepIndex = Math.max(dialogSteps.length - 1, 0);
        setActiveStep((prev) => Math.min(prev, lastStepIndex));
        setMaxReachedStep((prev) => Math.min(prev, lastStepIndex));
        handlersRef.current = Object.fromEntries(
            Object.entries(handlersRef.current).filter(
                ([k]) => Number(k) < dialogSteps.length,
            ),
        );
        setCompletedMap((prev) =>
            Object.fromEntries(
                Object.entries(prev).filter(
                    ([k]) => Number(k) < dialogSteps.length,
                ),
            ),
        );
        setDisabledMap((prev) =>
            Object.fromEntries(
                Object.entries(prev).filter(
                    ([k]) => Number(k) < dialogSteps.length,
                ),
            ),
        );
        setInvalidSteps((prev) => prev.filter((i) => i < dialogSteps.length));
    }, [dialogSteps.length]);

    const goToStep = useCallback((index: number, options?: GoToStepOptions) => {
        if (options?.keepToast) {
            skipNextToastClearRef.current = true;
        }

        setActiveStep(index);
        setMaxReachedStep((prev) => Math.max(prev, index));
    }, []);

    const isLastStep = activeStep === dialogSteps.length - 1;
    const primaryDisabled = !!disabledMap[activeStep];
    const allPreviousStepsCompleted = dialogSteps
        .slice(0, -1)
        .every((_, index) => completedMap[index] === true);
    const allStepsCompleted =
        linear &&
        !isLastStep &&
        maxReachedStep >= dialogSteps.length - 1 &&
        allPreviousStepsCompleted &&
        dialogSteps.every((_, index) => disabledMap[index] === false);
    const invalidStepSet = useMemo(() => new Set(invalidSteps), [invalidSteps]);
    const nextAccessibleIndex = activeStep + 1;
    const showNextAccessible =
        linear && !primaryDisabled && nextAccessibleIndex < dialogSteps.length;

    const canNavigateToStep = useCallback(
        (targetIndex: number): boolean => {
            if (!linear) {
                return true;
            }

            if (targetIndex <= activeStep) {
                return true;
            }

            if (primaryDisabled) {
                return false;
            }

            const isImmediateNextStep = targetIndex === activeStep + 1;
            const isReachedForwardStep = targetIndex <= maxReachedStep;
            return isImmediateNextStep || isReachedForwardStep;
        },
        [linear, activeStep, primaryDisabled, maxReachedStep],
    );

    const setStepCompleted = useCallback(
        (index: number, completed: boolean) => {
            setCompletedMap((prev) => {
                if (completed) {
                    if (prev[index] === true) {
                        return prev;
                    }

                    return {
                        ...prev,
                        [index]: true,
                    };
                }

                if (!(index in prev)) {
                    return prev;
                }

                const { [index]: _removed, ...rest } = prev;
                return rest;
            });
        },
        [],
    );

    const getStepHeaderState = useCallback(
        (index: number): StepHeaderState => {
            if (!linear) {
                return {
                    reached: true,
                    completed: false,
                    nextAccessible: false,
                    invalid: invalidStepSet.has(index),
                    disabled: false,
                    canInteract: true,
                };
            }

            const reached = index <= maxReachedStep;
            const nextAccessible =
                showNextAccessible && index === nextAccessibleIndex;
            const canInteract = reached || nextAccessible;

            return {
                reached,
                completed: completedMap[index] === true,
                nextAccessible,
                invalid: invalidStepSet.has(index),
                disabled: !canInteract,
                canInteract,
            };
        },
        [
            linear,
            invalidStepSet,
            maxReachedStep,
            showNextAccessible,
            nextAccessibleIndex,
            completedMap,
        ],
    );

    const getSeparatorState = useCallback(
        (index: number): { reached: boolean; nextAccessible: boolean } => {
            if (!linear) {
                return { reached: true, nextAccessible: false };
            }

            const targetStepIndex = index + 1;
            return {
                reached: targetStepIndex <= maxReachedStep,
                nextAccessible:
                    showNextAccessible &&
                    targetStepIndex === nextAccessibleIndex,
            };
        },
        [linear, maxReachedStep, showNextAccessible, nextAccessibleIndex],
    );

    const registerProceed = useCallback(
        (handler: () => boolean | Promise<boolean>, index: number) => {
            handlersRef.current[index] = handler;
        },
        [],
    );

    const setPrimaryDisabled = useCallback(
        (
            disabled: boolean,
            index: number,
            options?: SetPrimaryDisabledOptions,
        ) => {
            if (disabled) {
                setStepCompleted(index, false);
            }
            setDisabledMap((prev) => {
                if (prev[index] === disabled) {
                    return prev;
                }

                return {
                    ...prev,
                    [index]: disabled,
                };
            });
            const isInvalid = options?.isInvalid;

            if (!disabled) {
                stepEverValidRef.current.add(index);
                setInvalidSteps((prev) =>
                    prev.includes(index)
                        ? prev.filter((s) => s !== index)
                        : prev,
                );
            } else if (isInvalid === false) {
                setInvalidSteps((prev) =>
                    prev.includes(index)
                        ? prev.filter((s) => s !== index)
                        : prev,
                );
            } else if (
                isInvalid === true ||
                stepEverValidRef.current.has(index)
            ) {
                setInvalidSteps((prev) =>
                    prev.includes(index) ? prev : [...prev, index],
                );
            }
        },
        [setStepCompleted],
    );

    const showToast = useCallback((message: ToastMessage) => {
        toastRef.current?.show({
            content: ToastContent,
            ...message,
        });
    }, []);

    useEffect(() => {
        if (skipNextToastClearRef.current) {
            skipNextToastClearRef.current = false;
            return;
        }
        toastRef.current?.clear();
    }, [activeStep]);

    const showStepInvalidToast = useCallback(() => {
        showToast({
            severity: 'error',
            detail: 'Please complete all required fields before continuing.',
            life: 6000,
        });
    }, [showToast]);

    const waitForProceedHandler = useCallback(
        async (
            index: number,
        ): Promise<(() => boolean | Promise<boolean>) | undefined> => {
            const timeoutMs = 1000;
            const pollIntervalMs = 20;
            const start = Date.now();

            while (Date.now() - start <= timeoutMs) {
                const handler = handlersRef.current[index];
                if (handler) {
                    return handler;
                }

                await new Promise<void>((resolve) => {
                    setTimeout(resolve, pollIntervalMs);
                });
            }

            return undefined;
        },
        [],
    );

    const handleStepHeaderClickCapture = useCallback(
        (event: ReactMouseEvent<HTMLElement>) => {
            if (!linear) {
                return;
            }

            const target = event.target as HTMLElement;
            const header = target.closest('.p-stepper-header');
            if (!header) {
                return;
            }

            const targetIndexValue = header.getAttribute('data-ics-step-index');
            const targetIndex =
                targetIndexValue === null ? NaN : Number(targetIndexValue);
            if (Number.isNaN(targetIndex)) {
                return;
            }

            if (!canNavigateToStep(targetIndex)) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [linear, canNavigateToStep],
    );

    const handleProceed = useCallback(async () => {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        setLoading(true);
        try {
            if (!linear) {
                const invalidStepIndexes: number[] = [];

                for (
                    let index = 0;
                    index < dialogSteps.length - 1;
                    index += 1
                ) {
                    const handler = handlersRef.current[index];
                    if (!handler) {
                        continue;
                    }

                    const isStepValid = await handler();
                    if (!isStepValid) {
                        invalidStepIndexes.push(index);
                    }
                }

                if (invalidStepIndexes.length > 0) {
                    const firstInvalidStepIndex = invalidStepIndexes[0];
                    setInvalidSteps(invalidStepIndexes);
                    goToStep(firstInvalidStepIndex, { keepToast: true });
                    showStepInvalidToast();
                    return;
                }

                setInvalidSteps([]);
                const lastStepIndex = dialogSteps.length - 1;
                let submitHandler:
                    | (() => boolean | Promise<boolean>)
                    | undefined = handlersRef.current[lastStepIndex];
                if (!submitHandler) {
                    goToStep(lastStepIndex, { keepToast: true });
                    submitHandler = await waitForProceedHandler(lastStepIndex);
                }

                if (!submitHandler) {
                    showToast({
                        severity: 'error',
                        detail: 'Unable to submit. Please try again.',
                        life: 6000,
                    });
                    return;
                }

                const submitResult = await submitHandler();
                if (submitResult) {
                    forceClose();
                }
                return;
            }

            const handler = handlersRef.current[activeStep];
            if (!handler) {
                return;
            }

            if (isLastStep) {
                const invalidStepIndexes: number[] = [];

                for (
                    let index = 0;
                    index < dialogSteps.length - 1;
                    index += 1
                ) {
                    const stepHandler = handlersRef.current[index];
                    if (!stepHandler) {
                        continue;
                    }

                    const isStepValid = await stepHandler();
                    if (!isStepValid) {
                        invalidStepIndexes.push(index);
                    }
                }

                if (invalidStepIndexes.length > 0) {
                    setInvalidSteps(invalidStepIndexes);
                    invalidStepIndexes.forEach((index) => {
                        setStepCompleted(index, false);
                    });
                    goToStep(invalidStepIndexes[0], { keepToast: true });
                    showStepInvalidToast();
                    return;
                }

                setInvalidSteps([]);
                const submitResult = await handler();
                if (submitResult) {
                    forceClose();
                }
                return;
            }

            const result = await handler();
            if (!result) {
                setInvalidSteps([activeStep]);
                setStepCompleted(activeStep, false);
                showStepInvalidToast();
                return;
            }
            setInvalidSteps([]);
            setStepCompleted(activeStep, true);

            if (allStepsCompleted) {
                const lastHandler = handlersRef.current[dialogSteps.length - 1];
                if (lastHandler) {
                    const submitResult = await lastHandler();
                    if (submitResult) {
                        forceClose();
                    }
                }
                return;
            }

            goToStep(activeStep + 1);
        } finally {
            setLoading(false);
            isSubmittingRef.current = false;
        }
    }, [
        activeStep,
        dialogSteps.length,
        linear,
        forceClose,
        setLoading,
        goToStep,
        isLastStep,
        allStepsCompleted,
        showStepInvalidToast,
        setStepCompleted,
        waitForProceedHandler,
        showToast,
    ]);

    const handleRequestClose = useCallback((): void => {
        if (confirmClose?.isDirty()) {
            setConfirmVisible(true);
        } else {
            forceClose();
        }
    }, [confirmClose, forceClose]);

    const dialogClassName = clsx('dialog-stepper', size, propsDialogClassName);

    const closeButton = buttons?.closeButton;
    const submitLabel = buttons?.submitLabel ?? 'Submit';
    const submitDisabled =
        typeof buttons?.submitDisabled === 'function'
            ? buttons.submitDisabled()
            : Boolean(buttons?.submitDisabled);
    const shouldShowStepperNavigation = dialogSteps.length > 1;
    const activeStepContent = dialogSteps[activeStep];
    const stepContent = (
        <div className="dialog-stepper-content">
            {activeStepContent ? (
                <StepIndexProvider key={activeStep} index={activeStep}>
                    <StepContentWrapper
                        content={activeStepContent.content}
                        index={activeStep}
                        registerProceed={registerProceed}
                        setPrimaryDisabled={setPrimaryDisabled}
                        showToast={showToast}
                    />
                </StepIndexProvider>
            ) : null}
        </div>
    );
    const stepperNavigation = shouldShowStepperNavigation ? (
        <PillStepper
            className={clsx(linear && primaryDisabled && 'ics-stepper-locked')}
            activeStep={activeStep}
            onHeaderClickCapture={handleStepHeaderClickCapture}
            onChangeStep={(event) => {
                if (!linear) {
                    goToStep(event.index);
                    return;
                }

                const canGoBack = event.index <= maxReachedStep;
                const canGoForward =
                    event.index === activeStep + 1 && !primaryDisabled;
                if (canGoBack || canGoForward) {
                    if (
                        event.index !== activeStep &&
                        disabledMap[activeStep] === false
                    ) {
                        setStepCompleted(activeStep, true);
                    }
                    goToStep(event.index);
                }
            }}
            items={dialogSteps.map((step: StepConfig, index: number) => {
                const headerState = getStepHeaderState(index);
                const separatorState = getSeparatorState(index);

                return {
                    id: String(index),
                    title: `${index + 1}. ${step.title}`,
                    reached: headerState.reached,
                    completed: headerState.completed,
                    nextAccessible: headerState.nextAccessible,
                    invalid: headerState.invalid,
                    disabled: headerState.disabled,
                    canInteract: headerState.canInteract,
                    separatorReached: separatorState.reached,
                    separatorNextAccessible: separatorState.nextAccessible,
                };
            })}
        />
    ) : null;

    function ActionButtons(): ReactNode {
        const close = useDialogClose();
        const showSubmit = !linear || isLastStep || allStepsCompleted;
        return (
            <>
                <Button
                    loading={loading}
                    disabled={loading || (showSubmit && submitDisabled)}
                    label={showSubmit ? submitLabel : 'Next'}
                    icon={!showSubmit && <Icon name="arrow_right_alt" />}
                    iconPos="right"
                    onClick={handleProceed}
                />
                <PrimeButton
                    disabled={loading}
                    label={closeButton?.label ?? 'Close'}
                    severity="secondary"
                    onClick={() => {
                        if (closeButton?.callback) {
                            closeButton.callback();
                        }

                        if (!closeButton?.overrideDefault) {
                            close();
                        }
                    }}
                />
            </>
        );
    }

    return (
        <StepperDialogStepsContext.Provider
            value={{ setSteps: setDialogSteps }}
        >
            <Toast ref={toastRef} position="top-center" baseZIndex={10000} />
            <DialogWithDoubleEscape
                onClose={onClose}
                onBeforeClose={confirmClose ? handleRequestClose : undefined}
                doubleEscapeEnabled={!confirmVisible}
                className={dialogClassName}
                header={<HeaderContent />}
                footer={size !== DialogSize.sm ? undefined : <FooterContent />}
                pt={pt}
                closeRef={closeRef}
            >
                {stepperNavigation}
                {wrapper ? (
                    <wrapper.component {...wrapper.props}>
                        {stepContent}
                    </wrapper.component>
                ) : (
                    stepContent
                )}
            </DialogWithDoubleEscape>
            {confirmClose && (
                <CloseConfirmationDialog
                    visible={confirmVisible}
                    config={confirmClose}
                    onCancel={() => setConfirmVisible(false)}
                    onConfirm={() => {
                        setConfirmVisible(false);
                        forceClose();
                    }}
                />
            )}
        </StepperDialogStepsContext.Provider>
    );

    function HeaderContent(): ReactNode {
        return (
            <div className="flex flex-row justify-content-between align-items-center w-full">
                <div className="flex flex-column gap-1">
                    <div className="flex align-items-center">{title}</div>
                    {subtitle ? (
                        <div className="text-secondary text-sm font-normal text-transform-none">
                            {subtitle}
                        </div>
                    ) : null}
                </div>
                <div className="flex flex-row gap-2">
                    <ActionButtons />
                </div>
            </div>
        );
    }

    function FooterContent(): ReactNode {
        return (
            <div className="flex flex-row gap-2">
                <ActionButtons />
            </div>
        );
    }
}

interface StepContentWrapperProps {
    content: ReactNode;
    index: number;
    registerProceed: (
        handler: () => boolean | Promise<boolean>,
        index: number,
    ) => void;
    setPrimaryDisabled: (
        disabled: boolean,
        index: number,
        options?: SetPrimaryDisabledOptions,
    ) => void;
    showToast: (message: ToastMessage) => void;
}

function StepContentWrapper({
    content,
    index,
    registerProceed,
    setPrimaryDisabled,
    showToast,
}: StepContentWrapperProps) {
    const contextValue = useMemo(
        () => ({
            registerProceed: (handler: () => boolean | Promise<boolean>) =>
                registerProceed(handler, index),
            setPrimaryDisabled: (
                disabled: boolean,
                options?: SetPrimaryDisabledOptions,
            ) => setPrimaryDisabled(disabled, index, options),
            showToast,
        }),
        [index, registerProceed, setPrimaryDisabled, showToast],
    );

    return (
        <StepperDialogContext.Provider value={contextValue}>
            {content}
        </StepperDialogContext.Provider>
    );
}

export type { ConfirmCloseConfig };
